import React, { useState } from 'react';
import { X, Phone, Send, Check, AlertCircle, Loader2 } from 'lucide-react';
import { checkWhatsAppNumberStatus, sendMessage } from '../../helper/backend_helper';

const StartChatModal = ({ isOpen, onClose }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formattedNumber, setFormattedNumber] = useState('');

  const formatPhoneNumber = (input) => {
    // Remove all non-numeric characters
    const cleaned = input.replace(/\D/g, '');

    // Return the cleaned number with @c.us suffix
    return `${cleaned}@c.us`;
  };

  const validatePhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    // International numbers should be at least 10 digits and at most 15 digits
    return cleaned.length >= 10 && cleaned.length <= 15;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);
    setError('');
    setIsVerified(false);
    setSuccess(false);

    if (validatePhoneNumber(value)) {
      setFormattedNumber(formatPhoneNumber(value));
    } else {
      setFormattedNumber('');
    }
  };

  const checkNumberStatus = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      setError('Please enter a valid international phone number');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      // Replace with your actual API endpoint
      const response = await checkWhatsAppNumberStatus({ phoneNumber: formattedNumber }, {});

      const data = await response.data;

      setIsVerified(true);
    } catch (error) {
      console.log(error.response?.data?.errors[0]?.detail);
      const status = error.response?.status;

      const message =
        (status >= 400 && status < 500 && error.response?.data?.errors[0]?.detail) ||
        error.response?.data?.message ||
        (error.request ? 'No response from server. Please check your connection.' : error.message) ||
        'Failed to verify number. Please try again.';

      setError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setIsSending(true);
    setError('');

    try {
      const data = {
        message: message.trim(),
        messageType: 'text',
        type: 'WhatsApp',
        contactNo: formattedNumber,
        messageTime: new Date(),
      };

      const response = await sendMessage(data, {});

      setSuccess(true);
      setTimeout(() => {
        onClose();
        resetForm();
      }, 3000);
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const resetForm = () => {
    setPhoneNumber('');
    setMessage('');
    setIsVerified(false);
    setError('');
    setSuccess(false);
    setFormattedNumber('');
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      {/* <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={handleClose} /> */}

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Phone className="w-5 h-5 text-green-600" />
            Start Chat
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Phone Number Input */}
          <div>
            <div htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </div>
            <div className="space-y-2">
              <input
                type="tel"
                name="phoneNumber"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="+1234567890 or 1234567890"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                  formattedNumber && !error
                    ? 'border-green-300 bg-green-50'
                    : error
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                }`}
              />
              <p className="text-xs text-gray-500">
                Enter with or without country code (e.g., +1234567890 or 1234567890)
              </p>
              {formattedNumber && (
                <p className="text-xs text-green-600 font-medium">Will be formatted as: {formattedNumber}</p>
              )}
            </div>

            {/* Verify Button */}
            {formattedNumber && !isVerified && (
              <button
                onClick={checkNumberStatus}
                disabled={isVerifying}
                className="mt-3 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Number'
                )}
              </button>
            )}

            {/* Verification Status */}
            {isVerified && (
              <div className="mt-3 flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                <Check className="w-5 h-5" />
                <span className="text-sm font-medium">Number verified!</span>
              </div>
            )}
          </div>

          {/* Message Input */}
          {isVerified && (
            <div className="animate-in slide-in-from-top duration-300">
              <div className="block text-sm font-medium text-gray-700 mb-2">Message</div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition-all"
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <Check className="w-5 h-5" />
              <span className="text-sm">Message sent successfully!</span>
            </div>
          )}
        </div>

        {/* Footer */}
        {isVerified && !success && (
          <div className="p-6 pt-0">
            <button
              onClick={handleSendMessage}
              disabled={isSending || !message.trim()}
              className="w-full bg-[#1E3A5F] text-white py-3 px-4 rounded-lg hover:bg-[#142A47] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-colors"
            >
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Message
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartChatModal;
